<template>
  <Chart v-if="showChart" ref="chartContainer" :options="chartOptions" style="width: auto; height: auto;"></Chart>
</template>

<script setup>
import {computed, nextTick, onBeforeMount, onMounted, ref, watch} from 'vue'
import Highcharts from 'highcharts';
import {Chart} from 'highcharts-vue'
import highchartsMore from 'highcharts/highcharts-more'
import formula from '@jspreadsheet/formula-pro'
import tinycolor from "tinycolor2";
import useComputedTreeValue from "@/views/components/element/hooks/useComputedTreeValue.ts";
import {useGivingTreeStore, useSlidesStore} from "@/store/index.ts";
import {storeToRefs} from "pinia";

formula.license('NTYzN2FkOGQ2NGU5YzliMGY4OGYzMjdhN2EyMWUxOTA4MTE5M2MwOTEyYWZhNmEyY2E1ZjNhNWI0YjI1OTEzYzUyYTBkY2JjMDE1ZThmOTAwNDcyYWJiODMwZWJlZjlhNTMyNDIxMTE4MWZhNDI0MWIxNmI5OThmNzhhZjNjNWQsZXlKamJHbGxiblJKWkNJNklqSmhaRGsyWTJNeE5tVTJNalV3TVRjek1qZGlPV0ZoT0RjM1ltSmtaR1ppTXpVek1tRTNNVGdpTENKdVlXMWxJam9pVW1GeGRXVnNJRWRoY21OcFlTSXNJbVJoZEdVaU9qRTNOVEF6TnpRd01EQXNJbVJ2YldGcGJpSTZXeUpzWlhabGNtRm5aWEJ2YVc1MExtTnZiU0lzSWpFeU55NHdMakF1TVRvNE9EZzRJaXdpTVRJM0xqQXVNQzR4SWl3aWQyVmlJaXdpYkc5allXeG9iM04wSWwwc0luQnNZVzRpT2lJek15SXNJbk5qYjNCbElqcGJJblkzSWl3aWRqZ2lMQ0oyT1NJc0luWXhNQ0lzSW5ZeE1TSXNJbVp2Y20xMWJHRWlMQ0ptYjNKdGN5SXNJbkpsYm1SbGNpSXNJbkJoY25ObGNpSXNJbWx0Y0c5eWRHVnlJaXdpZG1Gc2FXUmhkR2x2Ym5NaUxDSmpiMjF0Wlc1MGN5SXNJbk5sWVhKamFDSXNJbU5vWVhKMGN5SmRmUT09')

highchartsMore(Highcharts)
const givingTreeStore = useGivingTreeStore()
const { updated, globalCalculations } = storeToRefs(givingTreeStore)
const { getActiveHookId, getComputedValue, getValueByHookId, getComputedType, getComputedTypeByHookId, getComputedPrecision, getComputedPrecisionByHookId, getComputedTextByHookId, getNotesByHookId } = useComputedTreeValue()

const {currentSlide, currency, locale, theme} = storeToRefs(useSlidesStore())


const props = defineProps({
  data: Object,
  themeColor: Array,
  type: String,
  title: String,
  options: Object,
  jspreadsheet: Object,
  compact: Boolean,
  width: Number,
  height: Number
})

const chartContainer = ref(null)
const mappings = ref({})
const createMapping = (hookId) => computed(() => getActiveHookId(hookId))
const fontName = computed(() => `"${theme.value.fontName}"` ?? '')
const enableTooltips = computed(() => props.options.tooltips ?? true)
const toolTipFontColor = computed(() => props.options?.tooltipFontColor ?? '#000')
const toolTipFontSize = computed(() => props.options?.tooltipFontSize ?? '20px')
const enableDataLabels = computed(() => props.options.dataLabels ?? true)
const dataLabelColors = computed(() => props.options.dataLabelsColor ?? '#000')
const dataLabelLocation = computed(() => props.options.dataLabelLocation ?? 'Outside')
const dataLabelType = computed(() => props.options.dataLabelType ?? 'Name')
const enableAxisY = computed(() => props.options.axisY ?? true)
const enableAxisX = computed(() => props.options.axisX ?? true)
const metaCurrency = computed(() => {
  return getComputedTextByHookId('meta-currency') ?? 'USD'
})
const metaLocale = computed(() => locale.value ?? 'en-US')

const enableCompact = computed(() => props.compact ?? false)

const showChart = ref(true)
onMounted(() => {
  if (currentSlide.value.infiniteScroll) window.addEventListener('resize', handleResize)
})

const handleResize = () => {
  showChart.value = false
  nextTick(() => {
    showChart.value = true
  })
};

const dataLabelFormatter = computed(() => {
  if (enableCompact.value) {
    return function() {
      if (this.point.y === 0) {
        return '';
      } else {
        return formatNumber(this.point.y);
      }
    }
  } else {
    return function() {
      if (this.point.y === 0) {
        return '';
      } else {
        return formatNumber(this.point.y);
      }
    }
  }
})

const enablePlotOptions = computed(() => {
  const basePlotOptions = {
    dataLabels: {
      enabled: enableDataLabels.value,
      style: {
        color: dataLabelColors.value,
        textOutline: 'none'
      },
      formatter: dataLabelFormatter.value,
    },
    borderColor: 'black',
    borderWidth: 0,
    pointWidth: props.options.barThickness
  }

  if (props.options.stackBars) {
    return {
      series: {
        stacking: 'normal',
        ...basePlotOptions
      }
    }
  } else if (getChartType() === 'pie') {
    return {
      pie: {
        showInLegend: props.options.legendEnabled,
        ...basePlotOptions
      }
    }
  } else {
    return {
      series: basePlotOptions
    };
  }
})

const stackLabels = computed(() => {
  if (props.options.stackBars && props.options.stackLabels) {
    return {
      enabled: true,
      style: {
        fontWeight: 'bold',
        textOutline: 'none'
      },
      formatter: function () {
        return 'Total: ' + formatNumber(this.total)
      }
    }
  }
  else {
    return undefined
  }
})

const themeColors = computed(() => {
  let colors
  const themeColor = props.themeColor
  if (themeColor.length >= 10) colors = themeColor
  else {
    const len = themeColor.length
    const supplement = tinycolor(themeColor[len - 1]).analogous(10 + 1 - len).map(color => color.toHexString())
    colors = [...themeColor.slice(0, len), ...supplement]
  }
  return colors
})

const chartTooltips = computed(() => {
  if (getChartType() === 'column' && props.options.stackBars) {
    return {
      enabled: enableTooltips.value,
      style: {
        color: toolTipFontColor.value,
        fontSize: toolTipFontSize.value
      },
      formatter: function () {
        return `<b>${props.jspreadsheet.cols[this.series.index + 1].title}</b>: ${formatNumber(this.point.y)}`;
      }
    }
  } else {
    return {
      enabled: enableTooltips.value,
      style: {
        color: toolTipFontColor.value,
        fontSize: toolTipFontSize.value
      },
      formatter: function () {
        const name = this.point.name ? `${this.point.name}` : `${this.point.x}`
        return `<b>${name}</b>: ${formatNumber(this.point.y)}`;
      }
    }
  }
})
const filterOutZeros = computed(() => props.options.filterOutZeros ?? true)
const filterOutNegatives = computed(() => props.options.filterOutNegatives ?? true)

onBeforeMount(() => {
  updateValues()

  if (window['Reveal']) {
    window['Reveal'].addEventListener('slidechanged', function() {
      const chartInstance = chartContainer.value.chart
      if (chartInstance) {
        chartInstance.redraw()
        nextTick(() => {
          chartInstance.update({
            xAxis: {
              labels: {
                autoRotation: false
              }
            },
            legend: {
              enabled: false
            }
          })

          nextTick(() => {
            chartInstance.update({
              xAxis: {
                labels: {
                  autoRotation: false
                }
              },
              legend: {
                enabled: props.options.legendEnabled
              }
            })
          })


        })
      }
    })
  }
})

const handleFormulas = (data) => {
  if (!data) return []

  const variables = props.jspreadsheet ? props.jspreadsheet.data : {}

  const processCell = (cell, colIndex) => {
    if (cell === null || cell === undefined) return null
    if (colIndex === 0) return cell.toString()
    if (typeof cell === 'string' && cell.startsWith('=')) {
      return formula(cell.slice(1), variables)
    }
    return cell
  };

  const processRow = (row) => row.map((cell, colIndex) => processCell(cell, colIndex))

  const chartData = data.map((row, rowIndex) => (row ? processRow(row, rowIndex) : []))

  return chartData.filter(array => array[0] !== undefined);
}

const updateValues = () => {
  if (props.data.hookIds.length === 0) return

  props.data.hookIds.forEach(item => {
    if (item.name.startsWith('A')) {
      if (!item.selector) {
        props.jspreadsheet.data[item.name] = props.jspreadsheet.data[item.name]?.toString() || ' '
      } else {
        props.jspreadsheet.data[item.name] = getFirstColumnValue(item)
      }
    } else {
      props.jspreadsheet.data[item.name] = getOtherColumnValues(item.hookId)
    }
  })
}

const getFirstColumnValue = (item) => {
  const selected = item.selector
  if (!selected || selected === 'Name') {
    const computedText = getComputedTextByHookId(item.hookId)
    return computedText !== '<Model Text>' ? computedText : ' '
  }

  const computedValues = {
    'Notes': getNotesByHookId(item.hookId),
    'Value': getValueByHookId(item.hookId)
  }

  return computedValues[selected] ?? (props.jspreadsheet.data[item.name]?.toString() || ' ')
}

const getOtherColumnValues = (hookId) => {
  const value = getValueByHookId(hookId)
  const precision = getComputedPrecisionByHookId(hookId)
  return parseFloat(value?.toFixed(precision))
}

const getChartType = () => {
  const { type, options } = props

  if (type !== 'line' && type !== 'pie' && options.horizontalBars) return 'bar'

  if (type === 'line') {
    if (options.area) return 'area'
    if (options.scatter) return 'scatter'
    if (options.smooth) return 'spline'
  }

  if (type === 'column') {
    if (options.waterfall) return 'waterfall'
  }

  return type
}

const reactiveData = computed(() => {
  const spreadsheetData = props.jspreadsheet ? JSON.parse(JSON.stringify(props.jspreadsheet.data)) : null
  const { hookIds } = props.data

  if (!hookIds || hookIds.length === 0) {
    let filteredData = props.data.series;

    if (filterOutZeros.value) {
      filteredData = filteredData.filter(item => {
        return item.slice(1).some(value =>
            value !== 0 && value !== null && value !== ""
        )
      })
    }

    if (filterOutNegatives.value) {
      filteredData = filteredData.filter(item => item[1] >= 0)
    }

    const formulas = handleFormulas(filteredData)

    return {
      categories: formulas.map(item => item[0] === null || item[0] === '' ? '' : item[0].toString()) ?? [],
      chartData: handleFormulas(filteredData)
    }
  }

  hookIds.forEach(item => {
    if (item.name.startsWith('A')) return

    spreadsheetData[item.name] = getValueByHookId(item.hookId)
  })

  let originalData = handleSpreadsheetData(spreadsheetData).filter(item => item[0] !== '' && item[1] !== '')

  let filteredData = [...originalData];

  if (filterOutZeros.value) {
    filteredData = filteredData.filter(item => {
      return item.slice(1).some(value =>
          value !== 0 && value !== null && value !== ""
      )
    })
  }

  if (filterOutNegatives.value) {
    filteredData = filteredData.filter(item => item[1] >= 0)
  }

  const formulas = handleFormulas(filteredData)
  let chartData

  if (props.type === 'column' && props.options.waterfall) {
    chartData = formatDataForWaterfallChart(filteredData)
  } else if (props.type === 'column' && props.options.stackBars) {
    chartData = formatDataForStackedChart(filteredData)
  } else {
    chartData = formulas
  }

  return {
    categories: formulas.map(item => item[0] === null || item[0] === '' ? '' : item[0].toString()) ?? [],
    chartData
  }
})

// TODO: handle when user wants a single stacked chart
const formatDataForStackedChart = (data) => {
  const formulaFormatted = handleFormulas(data);
  const cleanedValues = formulaFormatted.filter(value => value !== "");
  const seriesData = [];

  const numberOfSeries = cleanedValues.reduce((max, item) => {
    const nonNullCount = item.slice(1).filter(value => value !== null && value !== '').length;
    return Math.max(max, nonNullCount);
  }, 0);

  for (let i = 1; i <= numberOfSeries; i++) {
    seriesData.push([]);
  }

  cleanedValues.forEach(item => {
    for (let i = 1; i <= numberOfSeries; i++) {
      seriesData[i - 1].push(item[i] !== null ? item[i] : null);
    }
  });

  return seriesData.map((data, index) => ({
    name: props.jspreadsheet.cols[index + 1].title,
    data: data.filter(value => value !== null),
    animation: false,
    dataLabels: {
      enabled: enableDataLabels.value,
      style: {
        color: dataLabelColors.value,
        textOutline: 'none'
      }
    }
  }));
}

const showPriceValue = computed(() => props.options.showPriceValue ?? true)

const generateYPlotLines = () => {

  if (!props.options.plotYAxis) {
    return null
  }

  return [{
      value: props.options.plotYAxisValue ?? 0,
      color: props.options.plotYAxisColor ?? '#333',

      // width: 2, // Line width
      // zIndex: 3,

      /* use this if we need to add a label to the plot line
      label: {
        text: 'Zero Line',
        align: 'right', // Alignment of the label
        style: {
          color: 'red',
          fontWeight: 'bold'
        }
      }
      */
    }]
}

const chartOptions = computed(() => {

  const options = {
    chart: {
      type: getChartType(),
      height: props.height,
      style: { fontSize: props.options.fontSize, fontFamily: fontName.value },
      animation: false,
      backgroundColor: 'rgba(255, 255, 255, 0)',
      marginBottom: props.options.legendEnabled || (getChartType() !== 'pie' && props.options.axisXLabelEnabled) ? 100 : 0
    },
    accessibility: {
      enabled: false
    },
    title: {
      text: props.title,
      style: {
        color: props.options.chartTitleColor ?? '#000'
      }
    },
    xAxis: {
      gridLineWidth: props.options.xAxisGridWidth,
      gridLineColor: props.options.xAxisGridColor,
      categories: reactiveData.value.categories,
      visible: enableAxisX.value,
      title: {
        text: props.options.axisXLabel ? props.options.axisXLabel : '',
        style: { color: props.options.axisXLabelColor }
      },
      labels: {
        enabled: props.options.axisXLabelEnabled ?? true,
        style: {
          fontSize: props.options.fontSize,
          textOverflow: 'none'
        },
        rotation: props.options.xAxisLabelRotation ?? -45,
        autoRotation: false
      }
    },
    yAxis: {
      lineWidth: 1,
      lineColor: '#666',
      gridLineWidth: props.options.yAxisGridWidth,
      gridLineColor: props.options.yAxisGridColor,
      stackLabels: stackLabels.value,
      visible: enableAxisY.value,
      title: {
        text: props.options.axisYLabel ? props.options.axisYLabel : '',
        style: { color: props.options.axisYLabelColor }
      },
      plotLines: generateYPlotLines(),
      labels: {
        enabled: props.options.axisYLabelEnabled ?? true,
        style: { fontSize: props.options.fontSize },
        formatter: function () {
          const axis = true
          return this.value === 0 ? '$0' : formatNumber(this.value, axis)
        }
      }
    },
    colors: themeColors.value,
    plotOptions: enablePlotOptions.value,
    series: props.options.stackBars && props.type === 'column' ?
        reactiveData.value.chartData :
        [{
          animation: false,
          legendType: 'point',
          innerSize: props.options.donut ? (props.options.donutSize ? `${props.options.donutSize}%` : '50%') : '',
          colorByPoint: true,
          data: reactiveData.value.chartData,
          events: {
            click: function (event) {
              console.log(event)
            }
          },
          dataLabels: {
            enabled: enableDataLabels.value,
            distance: dataLabelLocation.value === 'Inside' && props.type === 'pie' ? -60 : 20,
            formatter: function () {
              if (dataLabelType.value === 'Name') {
                return this.point.name ? `${this.point.name}` : `${this.point.x}`;
              } else if (dataLabelType.value === 'Value'){
                return `${formatNumber(this.point.y)}`;
              } else if (dataLabelType.value === 'Both') {
                return `${this.point.name}: </br> ${formatNumber(this.point.y)}`
              }
            },
            inside: true, // Adjust this if you want the labels inside or outside the bars
            style: {
              color: dataLabelColors.value,
              textOutline: 'none' // Prevents the text from having a white outline
            },
            verticalAlign: props.type === 'bar' && dataLabelLocation.value === 'Outside'
                ? 'right'
                : props.type === 'column' && dataLabelLocation.value === 'Outside'
                    ? 'top'
                    : 'middle',
            align: 'center',
            x: props.type === 'bar' && dataLabelLocation.value === 'Outside' ? 25 : 0,
            y: props.type === 'column' && dataLabelLocation.value === 'Outside' ? -25 : 0
          }
        }
        ],

    legend: {
      enabled: props.options.legendEnabled,
      layout: props.options.legendLayout ? props.options.legendLayout.toLowerCase() : 'horizontal',
      align: props.options.legendAlign ? props.options.legendAlign.toLowerCase() : 'left',
      verticalAlign: props.options.legend ? props.options.legend.toLowerCase() : 'bottom',
    },
    tooltip: chartTooltips.value,
    credits: false
  }

  if (props.type === 'column' && props.options.waterfall && showPriceValue.value) {

    options.xAxis.categories[ reactiveData.value.chartData.length ] = 'Price / Value'

    const ourPriceColor = props.themeColor.length > reactiveData.value.chartData.length ? props.themeColor[reactiveData.value.chartData.length] : '#FCE367'
    const ourValueColor = props.themeColor.length > reactiveData.value.chartData.length + 1 ? props.themeColor[reactiveData.value.chartData.length + 1] : '#CCCCCC'

    options.series.push({
      type: 'column',
      animation: false,
      stacking: 'normal',
      name: 'Price / Value',
      data: [{
        x: reactiveData.value.chartData.length,
        y: globalCalculations.value.ourPrice.value,
        name: 'Price',
        color: ourPriceColor
      },
        {
          x: reactiveData.value.chartData.length,
          y: globalCalculations.value.differentiationValue.value,
          name: 'Value',
          color: ourValueColor
        }],
    })
  }

  return options
});

const handleSpreadsheetData = (inputData) => {
  const data = []
  for (const [key, value] of Object.entries(inputData)) {
    const col = key.charCodeAt(0) - 65 // Convert column letter to index
    const row = parseInt(key.slice(1)) - 1 // Convert row number to index
    if (!data[row]) {
      data[row] = []
    }
    data[row][col] = value
  }
  return data
}

const formatNumber = (value, axis) => {
  if (value === 0) {
    return ''
  }

  if (props.compact) {
    return compactFormatting(value, axis)
  }

  const hookIds = props.data.hookIds
  const valueType = hookIds.length > 0 ? getComputedTypeByHookId(hookIds[0].hookId) : 'unit'
  //TODO: change to computed currency var
  if (valueType === 'currency') {
    return new Intl.NumberFormat(metaLocale.value, {
      style: 'currency',
      currency: metaCurrency.value,
      notation: 'standard',
      minimumFractionDigits: getComputedPrecision.value,
      maximumFractionDigits: getComputedPrecision.value
    }).format(value);
  }

  if (valueType === 'percent') {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: getComputedPrecision.value,
      maximumFractionDigits: getComputedPrecision.value
    }).format(value) + '%';
  }

  return new Intl.NumberFormat('en-US', {
    notation:  'standard',
  }).format(value);
}

const compactFormatting = (value, axis) => {
  const valueType = getComputedType.value

  if (valueType === 'currency') {
    return new Intl.NumberFormat(metaLocale.value, {
      style: 'currency',
      currency: metaCurrency.value,
      notation: (props.compact && value >= 10000) || (props.compact && axis)  ? 'compact': 'standard',
      compactDisplay: 'short'
    }).format(value)
  }

  if (valueType === 'percent') {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: getComputedPrecision.value,
      maximumFractionDigits: getComputedPrecision.value
    }).format(value) + '%'
  }

  return new Intl.NumberFormat('en-US', {
    notation: (value >= 10000 || value <= -10000) || axis ? 'compact': 'standard',
  }).format(value)
}

const formatDataForWaterfallChart = (data) => {
  const formulaFormatted = handleFormulas(data)
  let formattedData = formulaFormatted.map((item) => {
    return {
      name: item[0].trim(),
      y: item[1]
    };
  });
  // Set the last object to isSum: true
  if (formattedData.length > 0) {
    if (formattedData.find(item => item.name === 'Value to You')) formattedData[formattedData.length - 2].y = (formattedData[formattedData.length - 2].y) * -1
    formattedData[formattedData.length - 1].isSum = true
  }

  return  formattedData
}

watch(() => props.data.hookIds, () => {
  if (props.data.hookIds) {
    for (const hookId of props.data.hookIds) {
      mappings.value[hookId] = createMapping(hookId)
    }
  }
}, {deep: true})

//TODO: bringing back for now to recalculate vd's
watch(updated, (newValue) => {
  if (newValue) {
    updateValues();
    updated.value = false;
  }
});

</script>



